/**
 * Predicate for the mobile perform-audit question-nav accordion.
 *
 * Applied on top of the chip-row filter (Pass / Fail / N/A / Unanswered).
 * A question is visible iff BOTH the chip predicate AND this one return
 * true — i.e. category + tag selections narrow further within whatever
 * the chip has already selected. AND across the two filter dimensions
 * so a Safety + `Area: Inbound` filter means "Safety AND Area=Inbound",
 * not "Safety OR Area=Inbound".
 *
 * Empty selections short-circuit to `true` so an unopened accordion is
 * a no-op — the caller doesn't have to guard `.length` at every call
 * site.
 */

// Locally-defined subset of `QuestionWithResponse` so this file doesn't
// couple to the SDK's exact shape. `optionalOverride: exactOptional`
// wants the explicit `| undefined` on optional props.
interface QuestionForFilter {
  questionDetails?:
    | {
        categories?: readonly string[] | undefined;
        tagsIncluded?: Record<string, readonly string[]> | undefined;
      }
    | undefined;
}

/**
 * A single (group, value) pair in the flattened tag selection.
 *
 * The underlying SDK shape is 2-level (`Record<string, string[]>`) —
 * we flatten to a list of pairs so a Stencil multi-select can hold
 * primitive-string option values (encode/decode happens at the
 * component boundary). This module doesn't care about encoding; it
 * takes the already-parsed pair shape.
 */
export interface TagSelection {
  readonly group: string;
  readonly value: string;
}

export interface QuestionFilterSelection {
  readonly selectedCategories: readonly string[];
  readonly selectedTags: readonly TagSelection[];
}

/**
 * Returns `true` when the question passes both the category filter and
 * the tag filter. See docblock at top of file for the AND-across-
 * dimensions rationale.
 *
 * Category match — any-of. `question.categories` and
 * `selectedCategories` must share at least one member. Empty selection
 * short-circuits to `true`.
 *
 * Tag match — any-of across pairs. At least one selected `(group,
 * value)` must appear in `question.tagsIncluded[group]`. Empty
 * selection short-circuits to `true`.
 *
 * Missing shapes on the question (`tagsIncluded === undefined`,
 * `categories === undefined`) are treated as "no matches available" —
 * a non-empty selection filters those questions OUT, which matches
 * user intent: "show me only questions tagged `Area: Inbound`" should
 * hide a question with no tags at all.
 */
export function matchesQuestionFilters(
  question: QuestionForFilter,
  selection: QuestionFilterSelection,
): boolean {
  const { selectedCategories, selectedTags } = selection;

  if (selectedCategories.length > 0) {
    const questionCategories = question.questionDetails?.categories ?? [];
    const categoryHit = questionCategories.some((c) => selectedCategories.includes(c));
    if (!categoryHit) return false;
  }

  if (selectedTags.length > 0) {
    const questionTags = question.questionDetails?.tagsIncluded;
    if (!questionTags) return false;
    const tagHit = selectedTags.some(({ group, value }) => {
      // Guarded lookup — never dynamic-index a record with a raw
      // string (lint + safety). Prototype-shadowed keys (`__proto__`)
      // would slip through a plain `questionTags[group]`.
      if (!Object.prototype.hasOwnProperty.call(questionTags, group)) return false;
      // eslint-disable-next-line security/detect-object-injection -- guarded by hasOwnProperty above; group is a user-selected tag-group name and never reaches Object.prototype
      const values = questionTags[group];
      return Array.isArray(values) && values.includes(value);
    });
    if (!tagHit) return false;
  }

  return true;
}

/**
 * Returns `true` when neither dimension has a selection — i.e. the
 * accordion is fully cleared. Convenience helper so consumers don't
 * duplicate the "is anything selected" check inline.
 */
export function isQuestionFilterEmpty(selection: QuestionFilterSelection): boolean {
  return selection.selectedCategories.length === 0 && selection.selectedTags.length === 0;
}

/** Stable empty selection — safe to use as an initial state or a
 *  "no selection" sentinel across renders. `readonly` so callers can't
 *  mutate the shared reference. */
export const EMPTY_QUESTION_FILTER_SELECTION: QuestionFilterSelection = {
  selectedCategories: [],
  selectedTags: [],
};
